
import { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { AuthContext } from '@/context/AuthContext';
import { permissionEngine } from '@/services/auth/permissionEngine';
import { SecurityEvent, EnhancedUser } from '@/context/auth/rbacTypes';

export const useEnhancedAuth = () => {
  const authContext = useContext(AuthContext);
  
  if (!authContext) {
    throw new Error('useEnhancedAuth must be used within an AuthProvider');
  }

  const {
    user,
    login,
    logout,
    isAuthenticated,
    hasRole,
    hasPermission
  } = authContext;

  // Enhanced state management for permissions
  const [permissionLoadingState, setPermissionLoadingState] = useState<{
    isLoading: boolean;
    loaded: boolean;
    lastLoadTime?: number;
  }>(() => {
    // For superusers, immediately mark permissions as loaded
    const initialLoaded = user?.isSuperuser || false;
    console.log('🚀 useEnhancedAuth initial state:', { 
      hasUser: !!user, 
      isSuperuser: user?.isSuperuser, 
      initialLoaded 
    });
    return {
      isLoading: false,
      loaded: initialLoaded
    };
  });

  // Transform User to EnhancedUser - memoized to prevent unnecessary re-renders
  const enhancedUser: EnhancedUser | null = useMemo(() => user ? {
    ...user,
    username: user.username || user.email,
    first_name: user.firstName || '',
    last_name: user.lastName || '',
    is_active: user.isActive || false,
    is_superuser: user.isSuperuser || false,
    permissions: user.permissions.map(permString => ({
      id: permString,
      name: permString,
      resource_id: permString.split('.')[0] || permString,
      action_id: permString.split('.')[1] || 'general'
    }))
  } : null, [user]);

  // Handle permission loading events
  useEffect(() => {
    if (!enhancedUser) {
      setPermissionLoadingState({ isLoading: false, loaded: false });
      return;
    }

    const handlePermissionsLoading = (data: { userId: number }) => {
      if (data.userId === enhancedUser.id) {
        console.log(`🔄 Permission loading started for user ${enhancedUser.id}`);
        setPermissionLoadingState(prev => ({ ...prev, isLoading: true }));
      }
    };

    const handlePermissionsLoaded = (data: { userId: number; permissions: Set<string> }) => {
      if (data.userId === enhancedUser.id) {
        console.log(`✅ Permissions loaded for user ${enhancedUser.id}:`, Array.from(data.permissions));
        setPermissionLoadingState({
          isLoading: false,
          loaded: true,
          lastLoadTime: Date.now()
        });
      }
    };

    const handlePermissionsError = (data: { userId: number; error: any }) => {
      if (data.userId === enhancedUser.id) {
        console.error(`❌ Permission loading error for user ${enhancedUser.id}:`, data.error);
        setPermissionLoadingState({
          isLoading: false,
          loaded: false
        });
      }
    };

    // Subscribe to permission events
    permissionEngine.addEventListener('permissionsLoading', handlePermissionsLoading);
    permissionEngine.addEventListener('permissionsLoaded', handlePermissionsLoaded);
    permissionEngine.addEventListener('permissionsError', handlePermissionsError);

    return () => {
      permissionEngine.removeEventListener('permissionsLoading', handlePermissionsLoading);
      permissionEngine.removeEventListener('permissionsLoaded', handlePermissionsLoaded);
      permissionEngine.removeEventListener('permissionsError', handlePermissionsError);
    };
  }, [enhancedUser?.id]);

  // Enhanced permission loading logic with timeout
  useEffect(() => {
    if (enhancedUser && isAuthenticated) {
      console.log(`🔐 Auth state changed for user ${enhancedUser.id}, checking permissions...`);
      
      if (enhancedUser.is_superuser) {
        console.log(`👑 Superuser detected, setting up immediate permissions`);
        permissionEngine.setupSuperuserPermissions(enhancedUser);
        setPermissionLoadingState({
          isLoading: false,
          loaded: true,
          lastLoadTime: Date.now()
        });
      } else {
        // Check if we already have valid permissions
        const hasValidCache = permissionEngine.hasValidCache(enhancedUser.id);
        console.log(`📋 Permission cache check for user ${enhancedUser.id}: ${hasValidCache ? 'valid' : 'invalid/missing'}`);
        
        if (hasValidCache) {
          setPermissionLoadingState({
            isLoading: false,
            loaded: true,
            lastLoadTime: Date.now()
          });
        } else {
          // Load permissions explicitly with timeout
          console.log(`🚀 Starting explicit permission load for user ${enhancedUser.id}`);
          setPermissionLoadingState(prev => ({ ...prev, isLoading: true }));
          
          // Add a timeout for permission loading
          const timeoutId = setTimeout(() => {
            console.error(`⏱️ Permission loading timeout for user ${enhancedUser.id}`);
            setPermissionLoadingState({
              isLoading: false,
              loaded: false
            });
          }, 30000); // 30 second timeout
          
          permissionEngine.preloadUserPermissions(enhancedUser).then(success => {
            clearTimeout(timeoutId);
            console.log(`📊 Permission preload result for user ${enhancedUser.id}: ${success ? 'success' : 'failed'}`);
            if (!success) {
              setPermissionLoadingState({
                isLoading: false,
                loaded: false
              });
            }
          }).catch(error => {
            clearTimeout(timeoutId);
            console.error(`❌ Permission preload error for user ${enhancedUser.id}:`, error);
            setPermissionLoadingState({
              isLoading: false,
              loaded: false
            });
          });
        }
      }
    } else {
      // Reset permission state when not authenticated
      setPermissionLoadingState({ isLoading: false, loaded: false });
    }
  }, [enhancedUser?.id, isAuthenticated, enhancedUser?.is_superuser]);

  const logSecurityEvent = useCallback((event: Omit<SecurityEvent, 'id' | 'timestamp' | 'user_id'>) => {
    console.log('🔒 Security Event:', event);
  }, []);

  // Enhanced permission check using permission engine
  const enhancedHasPermission = useCallback((permission: string, context?: any): boolean => {
    const result = permissionEngine.hasPermission(enhancedUser, permission, context);
    console.log(`🔍 Permission check: ${permission} = ${result} (user: ${enhancedUser?.id}, superuser: ${enhancedUser?.is_superuser})`);
    return result;
  }, [enhancedUser]);

  return {
    user: enhancedUser,
    login,
    logout,
    isAuthenticated,
    hasRole,
    hasPermission: enhancedHasPermission,
    isLoadingPermissions: permissionLoadingState.isLoading,
    permissionsLoaded: permissionLoadingState.loaded,
    logSecurityEvent
  };
};
